import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLotsList } from "@/features/lots/api/lotsApi";
import {
  LIFECYCLE_GROUP_LABEL,
  LIFECYCLE_LABEL,
  LIFECYCLE_STATES_BY_GROUP,
  type LifecycleGroup,
} from "../lifecycleStates";
import type { LifecycleState } from "../types";

export const SUB_STATE_FILTER_VALUES = ["lactando", "seca", "pronta"] as const;
export type SubStateFilterValue = (typeof SUB_STATE_FILTER_VALUES)[number];

const SUB_STATE_FILTER_LABEL: Record<SubStateFilterValue, string> = {
  lactando: "Lactando",
  seca: "Seca",
  pronta: "Pronta",
};

const ALL_VALUE = "__all__";

// Orden de grupos visibles en el dropdown de lifecycle — preserva el orden
// biológico (cría → desarrollo → productiva → retirada) consistente con
// design-standards y badges existentes.
const LIFECYCLE_GROUP_ORDER: LifecycleGroup[] = ["cria", "desarrollo", "productiva", "retirada"];

type Lot = { id: string; name: string };

type Props = {
  accountId: string;
  lifecycleFilter: LifecycleState | null;
  onLifecycleFilterChange: (value: LifecycleState | null) => void;
  lotFilter: string | null;
  onLotFilterChange: (value: string | null) => void;
  subStateFilter: SubStateFilterValue | null;
  onSubStateFilterChange: (value: SubStateFilterValue | null) => void;
};

export function HatoFilters({
  accountId,
  lifecycleFilter,
  onLifecycleFilterChange,
  lotFilter,
  onLotFilterChange,
  subStateFilter,
  onSubStateFilterChange,
}: Props) {
  const { data: lotsData } = useLotsList(accountId);
  const lots: Lot[] = lotsData?.lots ?? [];

  // El sub-estado solo aplica a cow_producing. Si el filtro de lifecycle está
  // en un estado distinto, el dropdown queda deshabilitado para evitar combos
  // sin sentido (ej. heifer_young + lactando).
  const subStateDisabled = lifecycleFilter !== null && lifecycleFilter !== "cow_producing";

  return (
    <div
      aria-label="Filtros del hato"
      className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center"
    >
      <Select
        value={lifecycleFilter ?? ALL_VALUE}
        onValueChange={(v) =>
          onLifecycleFilterChange(v === ALL_VALUE ? null : (v as LifecycleState))
        }
      >
        <SelectTrigger
          aria-label="Filtrar por estado"
          className="w-full md:w-[200px]"
          data-testid="filter-lifecycle"
        >
          <SelectValue placeholder="Estado: Todos" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>Estado: Todos</SelectItem>
          {LIFECYCLE_GROUP_ORDER.map((group) => (
            <SelectGroupLabel
              key={group}
              label={LIFECYCLE_GROUP_LABEL[group]}
              states={LIFECYCLE_STATES_BY_GROUP[group]}
            />
          ))}
        </SelectContent>
      </Select>

      <Select
        value={lotFilter ?? ALL_VALUE}
        onValueChange={(v) => onLotFilterChange(v === ALL_VALUE ? null : v)}
      >
        <SelectTrigger
          aria-label="Filtrar por lote"
          className="w-full md:w-[200px]"
          data-testid="filter-lot"
        >
          <SelectValue placeholder="Lote: Todos" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>Lote: Todos</SelectItem>
          {lots.map((lot) => (
            <SelectItem key={lot.id} value={lot.id}>
              {lot.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={subStateFilter ?? ALL_VALUE}
        onValueChange={(v) =>
          onSubStateFilterChange(v === ALL_VALUE ? null : (v as SubStateFilterValue))
        }
        disabled={subStateDisabled}
      >
        <SelectTrigger
          aria-label="Filtrar por sub-estado productivo"
          className="w-full md:w-[200px]"
          data-testid="filter-sub-state"
        >
          <SelectValue placeholder="Sub-estado: Todos" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>Sub-estado: Todos</SelectItem>
          {SUB_STATE_FILTER_VALUES.map((s) => (
            <SelectItem key={s} value={s}>
              {SUB_STATE_FILTER_LABEL[s]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SelectGroupLabel({
  label,
  states,
}: {
  label: string;
  states: LifecycleState[];
}) {
  return (
    <>
      <div className="px-1.5 pt-1.5 pb-0.5 font-medium text-[11px] text-app-ink-3 uppercase tracking-[0.06em]">
        {label}
      </div>
      {states.map((state) => (
        <SelectItem key={state} value={state}>
          {LIFECYCLE_LABEL[state]}
        </SelectItem>
      ))}
    </>
  );
}
